import { z } from 'zod';

/**
 * @aiPurpose realtime contract for the global `requests:updated` Socket.IO
 *   broadcast (#316). The wire payload is intentionally minimal — only the
 *   request id and a coarse reason — so connected clients can invalidate
 *   their TanStack Query cache and re-fetch through the existing REST
 *   visibility filter, without leaking request data over the WS channel.
 * @aiInvariant payload schema is `.strict()` — extra keys are a regression
 *   in the gateway and must fail validation. The gateway re-parses every
 *   payload through this schema before `socket.emit`, so a future bug that
 *   widens the shape produces a runtime error rather than a silent leak.
 * @aiSecurity see DANGER below — visibility re-check happens on the
 *   subsequent REST fetch, so the only fact this event leaks is "request
 *   <id> changed". Per-emit permission filter at the gateway gates
 *   `requests.read` to keep that fact away from un-permissioned sockets.
 */
export const REQUESTS_UPDATED = 'requests:updated' as const;

/**
 * @aiPurpose realtime contract for a request chat append notification.
 *   The payload deliberately carries only non-content metadata so a
 *   recipient can refetch the visibility-filtered chat REST endpoint
 *   without receiving message text, actor names, or request data over
 *   the socket.
 * @aiInvariant payload schema is `.strict()` and contains only
 *   `{requestId, messageId}`. Adding chat text or author/request data
 *   would violate the no-PII realtime invariant in this package.
 * @aiSecurity same audience gate as `requests:updated`: backend
 *   emits only to sockets whose live actor scope can see the parent
 *   request; the REST refetch then re-applies row-level visibility.
 */
export const REQUEST_CHAT_UPDATED = 'request-chat:updated' as const;

/**
 * @aiPurpose realtime contract for the request-chat read-state channel
 *   (#570). Fanned after read / mark-unread / unmark-unread mutations
 *   so connected clients can refetch the relevant chat detail
 *   (`kind === 'read'` may flip check-mark booleans) or unread summary
 *   (`'mark' | 'unmark'` is personal-state-only).
 * @aiInvariant payload is content-free: only `{requestId, kind}`. NO
 *   actor name, NO chat text, NO request details, NO permissions, NO
 *   unread count, NO exact `lastReadAt` timestamp. The frontend treats
 *   the event as a targeted refetch trigger that the REST visibility
 *   gate then re-validates.
 * @aiSecurity audience is a NARROW user-id allowlist gated by the
 *   gateway, NOT a row-level scope:
 *     - `kind: 'read'`   → marker + current managerId + current logistId.
 *     - `kind: 'mark'` / `kind: 'unmark'` → marker only (personal state).
 *   Live actor-state, JWT `tv`, and `requests.read` gates still apply
 *   per-socket before delivery; this event MUST NOT become a generic
 *   unaudited bypass for arbitrary realtime fan-out.
 */
export const REQUEST_CHAT_READ_UPDATED = 'request-chat-read:updated' as const;

/**
 * @aiPurpose Socket.IO logical namespace for the realtime channel.
 *   The gateway and the web client both bind to this string; keeping
 *   it in shared-realtime is the single source of truth so a typo on
 *   either side fails import-time, not at runtime.
 */
export const REALTIME_NAMESPACE = '/realtime' as const;

/**
 * @aiPurpose custom Engine.IO HTTP/WS path (see #316 review round 2).
 *   Default `/socket.io/` is unproxied by the dev Vite proxy and the
 *   prod reverse-proxy, both of which only forward the `/realtime`
 *   prefix. Nesting Engine.IO under `/realtime/socket.io` lets a
 *   single proxy rule cover both polling and the WS upgrade frame.
 *   MUST be identical on the gateway (`@WebSocketGateway({ path })`)
 *   and the web client (`io(url, { path })`).
 */
export const REALTIME_ENGINE_IO_PATH = '/realtime/socket.io' as const;

export const RequestsUpdatedReasonSchema = z.enum([
  'created',
  'updated',
  'transitioned',
  'deleted',
]);

export type RequestsUpdatedReason = z.infer<typeof RequestsUpdatedReasonSchema>;

// DANGER: keep the `.strict()` modifier. Anything wider would let a future
// bug widen the gateway's emit shape without a test catching it. The id
// regex mirrors `RequestSchema.id` in `@magimp/shared` (`LR-NNNNN`).
export const RequestsUpdatedEventSchema = z
  .object({
    id: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    reason: RequestsUpdatedReasonSchema,
  })
  .strict();

export type RequestsUpdatedEvent = z.infer<typeof RequestsUpdatedEventSchema>;

// DANGER: keep this payload content-free. Chat bodies and actor names can
// contain PII / commercial data; this event is only a targeted refetch trigger.
export const RequestChatUpdatedEventSchema = z
  .object({
    requestId: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    messageId: z.string().uuid(),
  })
  .strict();

export type RequestChatUpdatedEvent = z.infer<typeof RequestChatUpdatedEventSchema>;

/**
 * @aiPurpose discriminator for `REQUEST_CHAT_READ_UPDATED.kind`. Lets
 *   the frontend decide WHICH cache prefix to invalidate without
 *   over-fetching — `'read'` flips check-mark booleans on the focused
 *   chat, `'mark'`/`'unmark'` flips only the personal unread summary.
 * @aiInvariant `.enum([...])` is closed; extra values reject. Adding a
 *   new kind requires a coordinated frontend + backend + tests change.
 */
export const RequestChatReadUpdatedKindSchema = z.enum(['read', 'mark', 'unmark']);
export type RequestChatReadUpdatedKind = z.infer<typeof RequestChatReadUpdatedKindSchema>;

// DANGER: keep the `.strict()` modifier and the content-free shape.
// `requestId` mirrors `RequestChatUpdatedEventSchema.requestId` (same
// regex literal) so the realtime channel speaks one id grammar across
// the chat surface.
export const RequestChatReadUpdatedEventSchema = z
  .object({
    requestId: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    kind: RequestChatReadUpdatedKindSchema,
  })
  .strict();

export type RequestChatReadUpdatedEvent = z.infer<typeof RequestChatReadUpdatedEventSchema>;

/**
 * @aiPurpose realtime contract for the russia-requests-domain
 *   `russia-requests:updated` Socket.IO broadcast (#705 Slice 2a). Mirror
 *   of `requests:updated` but scoped to the new `RussiaRequest` aggregate
 *   (`RU-NNNNN` ids). Connected clients invalidate their TanStack Query
 *   cache and refetch through the REST visibility filter.
 * @aiInvariant payload schema is `.strict()`. It carries the RU-NNNNN id,
 *   a coarse `reason`, and (since #1580) OPTIONAL non-PII bid-scope
 *   metadata (`scope`/`scopeRefId`). The freeform `endAddress` on the
 *   aggregate is PII and MUST NOT enter this payload at any time.
 * @aiSecurity per-emit authorization mirrors the REST gate
 *   (`russiaRequests.read` precondition + row-audience check on
 *   `managerId`). See `apps/backend/src/realtime/realtime-delivery.service.ts`.
 * @aiPii freeform `endAddress` lives on the RussiaRequest aggregate and
 *   MUST NEVER leak into this payload, the log envelope around its
 *   emit, or any pino field name. The realtime channel is strictly a
 *   refetch trigger.
 */
export const RUSSIA_REQUESTS_UPDATED = 'russia-requests:updated' as const;

export const RussiaRequestsUpdatedReasonSchema = z.enum([
  'created',
  'updated',
  'transitioned',
  'deleted',
]);

export type RussiaRequestsUpdatedReason = z.infer<typeof RussiaRequestsUpdatedReasonSchema>;

// WHY (#1580): mirrors `RussiaCarrierBidScopeSchema` in `@magimp/shared`
//   (`REQUEST | START_CITY`). Declared locally because `@magimp/shared-realtime`
//   deliberately depends only on `zod` — it must not pull the full domain
//   package in to keep the wire contract self-contained. If the shared enum
//   gains a member, mirror it here.
export const RussiaRequestsUpdatedScopeSchema = z.enum(['REQUEST', 'START_CITY']);

export type RussiaRequestsUpdatedScope = z.infer<typeof RussiaRequestsUpdatedScopeSchema>;

// DANGER: keep the `.strict()` modifier. Anything wider would let a future
// bug widen the gateway's emit shape without a test catching it. The id
// regex mirrors `RussiaRequestSchema.id` in `@magimp/shared` (`RU-NNNNN`).
//
// `scope`/`scopeRefId` are the DELIBERATE #1580 additive bid-scope metadata:
// a per-city bid create/update or a per-city winner change carries the bid's
// `scope` and (for START_CITY) its `scopeRefId`. Both are OPTIONAL, so the
// pre-#1580 `{id, reason}` subscribers keep parsing unchanged. They are
// NON-PII reference data — `scopeRefId` is an `arrivalCityId`/scope enum,
// NEVER customer PII, NEVER `endAddress`. `.strict()` is preserved: declared
// optional keys stay strict-compatible and any UNDECLARED key (a real leak)
// is still rejected.
export const RussiaRequestsUpdatedEventSchema = z
  .object({
    id: z.string().regex(/^RU-\d{5}$/, 'must match RU-NNNNN'),
    reason: RussiaRequestsUpdatedReasonSchema,
    scope: RussiaRequestsUpdatedScopeSchema.optional(),
    scopeRefId: z.string().uuid().optional(),
  })
  .strict();

export type RussiaRequestsUpdatedEvent = z.infer<typeof RussiaRequestsUpdatedEventSchema>;

/**
 * @aiPurpose realtime contract for an RF chat append notification
 *   (#1066). Mirror of `REQUEST_CHAT_UPDATED` for the Russia capsule.
 *   The payload deliberately carries only non-content metadata so a
 *   recipient can refetch the visibility-filtered RF chat REST
 *   endpoint without receiving message text, actor names, RF details,
 *   `endAddress`, or `parentRequestId` over the socket.
 * @aiInvariant payload schema is `.strict()` and contains only
 *   `{russiaRequestId, messageId}`. Adding chat text, author, or RF
 *   row data would violate the no-PII realtime invariant and the
 *   #1063 China-parent whitelist.
 * @aiSecurity audience gate is the chat-party rule, STRICTLY
 *   narrower than `russia-requests:updated`'s row-visibility gate.
 *   Backend emits only to sockets that are admin, the row's manager
 *   (with `russiaRequests.transition.send`), or the assigned logist_ru
 *   (with `russiaRequests.scope.all` AND matching `logistId`). The
 *   same gate is used by the coarse `russia-requests:updated` fan-out
 *   triggered by chat-post — RF logist_ru classifies to `scope='all'`,
 *   so the broad row-visibility gate would otherwise over-fan to
 *   bystander logists. The REST refetch on the client then re-applies
 *   row-level visibility.
 * @aiPii RF `endAddress` is PII and MUST NEVER leak into this
 *   payload, the log envelope around its emit, or any pino field name.
 */
export const RUSSIA_REQUEST_CHAT_UPDATED = 'russia-request-chat:updated' as const;

// DANGER: keep this payload content-free. Chat bodies, RF row details,
// and `endAddress` are PII; this event is only a targeted refetch
// trigger. `russiaRequestId` mirrors `RussiaRequestsUpdatedEventSchema.id`
// so the realtime channel speaks one id grammar across the RF surface.
export const RussiaRequestChatUpdatedEventSchema = z
  .object({
    russiaRequestId: z.string().regex(/^RU-\d{5}$/, 'must match RU-NNNNN'),
    messageId: z.string().uuid(),
  })
  .strict();

export type RussiaRequestChatUpdatedEvent = z.infer<typeof RussiaRequestChatUpdatedEventSchema>;

/**
 * @aiPurpose realtime contract for the RF chat read-state channel
 *   (#1066). Fanned after read / mark-unread / unmark-unread mutations
 *   so connected clients can refetch the focused chat
 *   (`kind === 'read'`) or the unread summary (`'mark' | 'unmark'`).
 * @aiInvariant payload is content-free: only `{russiaRequestId, kind}`.
 *   NO actor name, NO chat text, NO RF details, NO `endAddress`, NO
 *   permissions, NO unread count, NO exact `lastReadAt` timestamp.
 * @aiSecurity audience is a NARROW user-id allowlist gated by the
 *   gateway:
 *     - `kind: 'read'`   → marker + current managerId + current logistId.
 *     - `kind: 'mark'` / `kind: 'unmark'` → marker only (personal).
 *   Live actor-state, JWT `tv`, and `russiaRequests.read` gates still
 *   apply per-socket before delivery.
 */
export const RUSSIA_REQUEST_CHAT_READ_UPDATED = 'russia-request-chat-read:updated' as const;

export const RussiaRequestChatReadUpdatedKindSchema = z.enum(['read', 'mark', 'unmark']);
export type RussiaRequestChatReadUpdatedKind = z.infer<
  typeof RussiaRequestChatReadUpdatedKindSchema
>;

// DANGER: keep the `.strict()` modifier and the content-free shape.
export const RussiaRequestChatReadUpdatedEventSchema = z
  .object({
    russiaRequestId: z.string().regex(/^RU-\d{5}$/, 'must match RU-NNNNN'),
    kind: RussiaRequestChatReadUpdatedKindSchema,
  })
  .strict();

export type RussiaRequestChatReadUpdatedEvent = z.infer<
  typeof RussiaRequestChatReadUpdatedEventSchema
>;

/**
 * @aiPurpose content-free refetch trigger for Lead ownership changes.
 * @aiInvariant the payload is exactly `{}`. Lead ids, owner ids, audience
 *   metadata, names, and contact data must never cross this channel.
 * @aiSecurity the backend computes the owner / previous-owner / leadership
 *   audience per socket; REST re-applies Lead visibility after invalidation.
 * @aiPii no PII or identifiers are carried on the wire.
 */
export const LEADS_UPDATED = 'leads:updated' as const;

/**
 * @aiPurpose content-free refetch trigger for durable Lead notifications.
 * @aiInvariant the payload is exactly `{}` and is intentionally separate from
 *   the request-only `notifications:updated` contract.
 * @aiSecurity delivery is restricted to the assignment target's sessions.
 * @aiPii no PII or identifiers are carried on the wire.
 */
export const LEAD_NOTIFICATIONS_UPDATED = 'lead-notifications:updated' as const;

// DANGER: keep both schemas empty and strict. Audience metadata and Lead/user
// ids are server-side delivery inputs, never wire payload fields.
export const LeadsUpdatedEventSchema = z.object({}).strict();
export type LeadsUpdatedEvent = z.infer<typeof LeadsUpdatedEventSchema>;

export const LeadNotificationsUpdatedEventSchema = z.object({}).strict();
export type LeadNotificationsUpdatedEvent = z.infer<typeof LeadNotificationsUpdatedEventSchema>;

/**
 * @aiPurpose realtime contract for the cross-domain notification
 *   aggregate channel (#1068). Fanned after a successful notification
 *   aggregate UPSERT (`kind: 'updated'`) or mark-viewed write
 *   (`kind: 'viewed'`). Connected web clients refetch their
 *   notification list / per-row badge cache; sound playback dedupes on
 *   the carried `aggregateId`.
 * @aiInvariant payload is strictly content-free: only the aggregate
 *   id + the logical target (`entityType`, `entityId`,
 *   `notificationType`, `routingTab`) + the `kind` discriminator.
 *   NO chat text, NO request titles, NO `endAddress` /
 *   `deliveryAddress`, NO insurance cargo value/currency, NO actor
 *   names, NO unread count, NO `lastEventAt` timestamp.
 * @aiSecurity audience is a NARROW user-id allowlist — only the
 *   notification's owner receives the event. Live actor-state, JWT
 *   `tv`, and authentication still apply per-socket before delivery.
 *   The aggregate row's `userId` is the audience source-of-truth; the
 *   gateway MUST NOT widen this beyond a single recipient.
 * @aiPii the realtime channel is a refetch trigger. Adding entity
 *   title / actor name / chat text to this payload would violate the
 *   no-PII invariant shared by the chat / RF surfaces.
 */
export const NOTIFICATIONS_UPDATED = 'notifications:updated' as const;

export const NotificationsUpdatedKindSchema = z.enum(['updated', 'viewed']);
export type NotificationsUpdatedKind = z.infer<typeof NotificationsUpdatedKindSchema>;

const NotificationEntityIdRegex =
  /^(?:(?:LR|RU)-\d{5}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

// DANGER: keep the `.strict()` modifier and the content-free shape.
// `entityId` admits both LR-NNNNN (China) and RU-NNNNN (RF) — the same
// id grammar the request domains speak. Any widening here is a PII
// regression: the realtime channel MUST stay a refetch trigger.
// INVARIANT (#1068, Codex F-005): superRefine correlates entityType
//   with entityId so CHINA_REQUEST + RU-NNNNN (or RUSSIA_REQUEST +
//   LR-NNNNN) reject at the wire boundary. The realtime channel speaks
//   the same correlated target grammar as the durable aggregate row.
export const NotificationsUpdatedEventSchema = z
  .object({
    aggregateId: z.string().uuid(),
    entityType: z.enum(['CHINA_REQUEST', 'RUSSIA_REQUEST', 'CARGO_PROBLEM']),
    entityId: z.string().regex(NotificationEntityIdRegex, 'must match a request code or UUID'),
    notificationType: z.enum([
      'chat',
      'rate',
      'rejection',
      'insurance',
      'responsibleStatus',
      'cargo_problem',
    ]),
    routingTab: z.enum(['info', 'items', 'boxes', 'carriers', 'chat', 'audit']).nullable(),
    kind: NotificationsUpdatedKindSchema,
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.entityType === 'CHINA_REQUEST' && !/^LR-\d{5}$/.test(val.entityId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'CHINA_REQUEST requires an LR-NNNNN entityId',
      });
    }
    if (val.entityType === 'RUSSIA_REQUEST' && !/^RU-\d{5}$/.test(val.entityId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'RUSSIA_REQUEST requires an RU-NNNNN entityId',
      });
    }
    if (val.entityType === 'CARGO_PROBLEM' && !z.string().uuid().safeParse(val.entityId).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'CARGO_PROBLEM requires a UUID entityId',
      });
    }
    if (val.notificationType === 'cargo_problem' && val.entityType !== 'CARGO_PROBLEM') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notificationType'],
        message: 'cargo_problem requires CARGO_PROBLEM',
      });
    }
  });

export type NotificationsUpdatedEvent = z.infer<typeof NotificationsUpdatedEventSchema>;
